<template>
  <Card class="overflow-hidden transition-all duration-200 w-full min-h-[300px]">
    <div
      v-if="loading"
      class="space-y-4 min-h-[250px]"
    >
      <Skeleton class="h-8 w-1/2 mb-6" />
      <Skeleton class="h-32" />
      <Skeleton class="h-24" />
    </div>
    <div
      v-else-if="error"
      class="p-4 text-center min-h-[250px] flex flex-col items-center justify-center"
    >
      <p class="text-red-500">
        {{ error }}
      </p>
      <Button
        class="mt-4"
        @click="fetchAIReview"
      >
        Try again
      </Button>
    </div>
    <template v-else-if="review && review.summary_text">
      <div class="mb-4">
        <h3 class="text-xl font-semibold flex items-center text-primary-700">
          <Icon
            name="i-heroicons-cpu-chip"
            class="mr-2 h-5 w-5"
          />
          Summary of {{ companyName }} Employee Feedback (AI-Generated)
        </h3>
        <div
          v-if="filterFallback"
          class="mt-2 flex items-center text-amber-600 text-sm"
        >
          <Icon
            name="i-heroicons-information-circle"
            class="mr-1 h-4 w-4"
          />
          <span>Limited data available for your filters. Using
            {{ filterFallbackDescription }}.</span>
        </div>
      </div>
      <div class="mb-6 border-b pb-4">
        <ReviewHeader
          :rating="review.average_rating || 0"
          :total-reviews="review.total_reviews || 0"
        />
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div class="space-y-6">
          <div class="max-h-64 overflow-y-auto pr-2 scrollbar">
            <SummarySection :summary="processedSummary" />
          </div>
          <div
            class="mt-4 text-xs text-gray-500 bg-gray-50 p-3 rounded-md border border-gray-200"
          >
            <p>
              <strong>Disclaimer:</strong> This review has been generated by AI
              based on employee feedback. It may not fully represent the company
              under the current filters. The AI attempts to analyze patterns in
              reviews but may not capture all nuances of the workplace
              experience.
            </p>
            <p
              v-if="filterFallback"
              class="mt-2"
            >
              <Icon
                name="i-heroicons-information-circle"
                class="inline-block mr-1 h-4 w-4"
              />
              Due to limited data for your exact filter criteria, this summary
              includes {{ filterFallbackDescription }}.
            </p>
          </div>
        </div>
        <div>
          <h3 class="text-lg font-semibold mb-4">
            Highlights
          </h3>
          <div class="space-y-4">
            <div
              v-if="review.pros?.length"
              class="space-y-2 p-4 bg-green-50 rounded-lg"
            >
              <h4 class="font-semibold text-green-700 flex items-center gap-2">
                <Icon
                  name="i-heroicons-check-circle"
                  class="w-5 h-5"
                />
                Pros
              </h4>
              <ul class="text-sm text-gray-700 list-disc pl-4 space-y-1">
                <li
                  v-for="(pro, index) in review.pros"
                  :key="index"
                >
                  {{ pro }}
                </li>
              </ul>
            </div>
            <div
              v-if="review.cons?.length"
              class="space-y-2 p-4 bg-red-50 rounded-lg"
            >
              <h4 class="font-semibold text-red-700 flex items-center gap-2">
                <Icon
                  name="i-heroicons-exclamation-circle"
                  class="w-5 h-5"
                />
                Cons
              </h4>
              <ul class="text-sm text-gray-700 list-disc pl-4 space-y-1">
                <li
                  v-for="(con, index) in review.cons"
                  :key="index"
                >
                  {{ con }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import ReviewHeader from './ReviewHeader.vue'
import SummarySection from './SummarySection.vue'
import { stateOptions } from '~/utils/states'

interface Theme {
  name: string
  frequency: number
  sentiment: 'positive' | 'negative'
}
interface Rating {
  category: string
  average: number
  count: number
  summary: string
}
interface AIReview {
  _id: string
  company_id: string
  soc_code_id: string
  location_id: string
  average_rating: number
  total_reviews: number
  summary_text: string
  pros: string[]
  cons: string[]
  ratings: Rating[]
  themes: Theme[]
  updated_at: string
}
const props = defineProps<{
  companyName: string
  permalink?: string
  role?: string
  state?: string
  city?: string
  roleName?: string
  preFetchedReview?: Partial<AIReview> | null
  isLoading?: boolean
  nationalLevel?: boolean
  filtered?: boolean
  filterLevel?: string
}>()
const emit = defineEmits(['has-content'])
const loading = ref(props.isLoading !== undefined ? props.isLoading : true)
const error = ref<string | null>(null)
const isFiltered = ref(false)
const filterLevel = ref('')
const filterFallback = ref(false)
const review = ref<AIReview | null>(null)
const getExpectedFilterLevel = () => {
  if (props.role && props.state && props.city) return 'exact'
  if (props.role && props.state) return 'role_state'
  if (props.role && !props.state && !props.city) return 'role'
  if (!props.role && props.state && props.city) return 'state_city'
  if (!props.role && props.state && !props.city) return 'state_abr'
  if (!props.role && !props.state && !props.city) return 'overall'
  return 'exact'
}
if (
  props.preFetchedReview
  && typeof props.preFetchedReview === 'object'
  && 'summary_text' in props.preFetchedReview
  && 'average_rating' in props.preFetchedReview
) {
  review.value = props.preFetchedReview as AIReview
  isFiltered.value = props.filtered !== undefined ? props.filtered : false
  filterLevel.value = props.filterLevel || ''
  if (isFiltered.value || filterLevel.value) {
    const expectedFilterLevel = getExpectedFilterLevel()
    filterFallback.value = filterLevel.value !== expectedFilterLevel
  }
}
const salaryTerms = [
  'salary',
  'salaries',
  'pay',
  'compensation',
  'wage',
  'wages',
  'earnings',
  'income',
  'remuneration'
]
const dressCodeTerms = [
  'dress code',
  'dress policy',
  'attire',
  'clothing',
  'uniform',
  'dresscode',
  'dress requirements'
]
const filterFallbackDescription = computed(() => {
  switch (filterLevel.value) {
    case 'role_state':
      return `${props.roleName || 'role'} in ${getStateName(props.state)}`
    case 'role':
      return `overall ${props.roleName || 'role'} reviews`
    case 'state_city':
      return `reviews from ${props.city}, ${getStateName(props.state)}`
    case 'state':
      return `reviews from ${getStateName(props.state)}`
    case 'overall':
      return 'overall company reviews'
    default:
      return 'broader results'
  }
})
const getStateName = (stateCode: string | undefined) => {
  if (!stateCode) return ''
  const state = stateOptions.find(s => s.code === stateCode)
  return state?.label || stateCode
}
const processedSummary = computed(() => {
  if (!props.permalink || !review.value || !review.value.summary_text)
    return ''
  let text = review.value.summary_text
  salaryTerms.forEach((term) => {
    const regex = new RegExp(`\\b${term}\\b`, 'gi')
    text = text.replace(
      regex,
      `<a href="/company/${props.permalink}/salaries" class="text-primary-600">$&</a>`
    )
  })
  dressCodeTerms.forEach((term) => {
    const regex = new RegExp(`\\b${term}\\b`, 'gi')
    text = text.replace(
      regex,
      `<a href="/company/${props.permalink}/dress-code" class="text-primary-600">$&</a>`
    )
  })
  text = text.replace(/\.\s+/g, '.<br>')
  if (text.endsWith('.')) {
    text = text.slice(0, -1) + '.<br>'
  }
  return text
})
const fetchAIReview = async () => {
  if (
    props.preFetchedReview
    && 'summary_text' in props.preFetchedReview
    && 'average_rating' in props.preFetchedReview
    && !loading.value
  ) {
    review.value = props.preFetchedReview as AIReview
    emit('has-content', true)
    return
  }
  loading.value = true
  error.value = null
  try {
    const response = await $fetch(
      `/api/companies/${props.permalink}/reviews/ai`,
      {
        method: 'POST',
        body: {
          role: props.role,
          state: props.state,
          city: props.city
        }
      }
    )
    if (response === undefined || response === null) {
      error.value = 'Received empty response from server'
      emit('has-content', false)
      return
    }
    if ('message' in response && response.message) {
      error.value = response.message
      emit('has-content', false)
      return
    }
    if (
      'aiReview' in response
      && response.aiReview
      && typeof response.aiReview === 'object'
      && 'summary_text' in response.aiReview
      && 'average_rating' in response.aiReview
    ) {
      review.value = response.aiReview as AIReview
      isFiltered.value = 'filtered' in response ? !!response.filtered : false
      filterLevel.value
        = 'filterLevel' in response ? (response.filterLevel as string) : ''
      if (isFiltered.value || filterLevel.value) {
        const expectedFilterLevel = getExpectedFilterLevel()
        filterFallback.value = filterLevel.value !== expectedFilterLevel
      }
      emit('has-content', true)
    } else {
      error.value = 'Invalid response format: missing aiReview data'
      emit('has-content', false)
    }
  } catch (err) {
    console.error('Error fetching AI review:', err)
    let errorMessage = 'Failed to generate AI review'
    if (err && typeof err === 'object' && 'data' in err && err.data) {
      errorMessage = err.data.message || errorMessage
    }
    error.value = errorMessage
    emit('has-content', false)
  } finally {
    loading.value = false
  }
}
onMounted(() => {
  if (
    props.preFetchedReview
    && 'summary_text' in props.preFetchedReview
    && 'average_rating' in props.preFetchedReview
  ) {
    review.value = props.preFetchedReview as AIReview
    isFiltered.value = props.filtered !== undefined ? props.filtered : false
    filterLevel.value = props.filterLevel || ''
    if (isFiltered.value || filterLevel.value) {
      const expectedFilterLevel = getExpectedFilterLevel()
      filterFallback.value = filterLevel.value !== expectedFilterLevel
    }
    loading.value = false
    emit('has-content', true)
  } else if (props.permalink) {
    fetchAIReview()
  }
})
watch(
  () => [
    props.permalink,
    props.role,
    props.state,
    props.city,
    props.preFetchedReview
  ],
  () => {
    if (
      props.preFetchedReview
      && 'summary_text' in props.preFetchedReview
      && 'average_rating' in props.preFetchedReview
    ) {
      review.value = props.preFetchedReview as AIReview
      loading.value = false
    } else if (props.permalink) {
      fetchAIReview()
    }
  },
  { deep: true }
)
</script>

<style scoped>
:deep(a) {
  color: rgb(var(--color-primary-600));
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s ease;
}
:deep(a:hover) {
  border-bottom-color: rgb(var(--color-primary-600));
}
.break-words {
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
}
.scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}
.scrollbar::-webkit-scrollbar {
  width: 6px;
}
.scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 20px;
}
</style>
